/**
 * remark plugin that rewrites human-authored user mentions in a markdown
 * AST into proper user-link nodes.
 *
 * Surface form:                 `@[Display Name](user_id)`
 * Rewritten link target:        `/users/<user_id>`
 * Rewritten link text:          `@Display Name`
 *
 * Critical detail: by the time this plugin runs, remark has ALREADY parsed
 * `[Display Name](user_id)` as a normal markdown link. The AST therefore
 * looks like `[ text("hi @"), link(url="user_id", children=[…]), … ]` —
 * not a single text node containing the literal `@[…](…)` source. The
 * walker matches that split shape: a `link` whose immediately-preceding
 * sibling is a text node ending in `@`.
 *
 * The `@` is moved INSIDE the link so the prefix and the name render with
 * a single underline / hover affordance instead of a stranded sigil.
 *
 * Code blocks (fenced or inline) and existing already-rewritten links are
 * skipped so a `@[name](id)` literal that appears inside a code sample
 * passes through unchanged, and the plugin is idempotent on already-
 * rewritten trees.
 */

interface MdastNode {
  type: string;
  value?: string;
  url?: string;
  children?: MdastNode[];
}

function rewriteLinkAsMention(prevText: MdastNode, link: MdastNode): void {
  // Strip the trailing `@` from the preceding text node — it now lives
  // inside the link.
  prevText.value = prevText.value!.slice(0, -1);

  const userId = link.url ?? "";
  link.url = `/users/${userId}`;

  // Prepend `@` to the link's existing children so emphasis / inline code
  // inside the display name survives.
  const first = link.children?.[0];
  if (first && first.type === "text" && first.value !== undefined) {
    first.value = `@${first.value}`;
  } else {
    link.children = [{ type: "text", value: "@" }, ...(link.children ?? [])];
  }
}

function walk(node: MdastNode): void {
  // Don't descend into code blocks (a `@[name](id)` literal inside code
  // should pass through verbatim) or into existing links (avoids double-
  // rewriting nested patterns).
  if (
    node.type === "code" ||
    node.type === "inlineCode" ||
    node.type === "link"
  ) {
    return;
  }
  if (!node.children) {
    return;
  }

  for (let i = 0; i < node.children.length; i++) {
    const child = node.children[i];
    if (child.type === "link" && i > 0) {
      const prev = node.children[i - 1];
      if (
        prev.type === "text" &&
        prev.value !== undefined &&
        prev.value.endsWith("@")
      ) {
        rewriteLinkAsMention(prev, child);
        // The link was just rewritten; don't descend into it.
        continue;
      }
    }
    walk(child);
  }
}

export function remarkUserMentions() {
  return (tree: MdastNode, _file?: unknown) => {
    walk(tree);
  };
}
